import * as actionTypes from "./actionTypes";
import {
  getGoalsAndObjectivesAPI,
  deleteGoalsAndObjectivesAPI,
  createGoalsAndObjectivesAPI,
  modifyGoalsAndObjectivesAPI,
  verifyGoalsAndObjectivesAPI,
  getGoalsAndObjectivesTasksAPI,
  getGoalsAndObjectivesOneAPI,
} from "@icarius-connection/api";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";

const errorHandler = (e, dispatch) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "INVALID_DATA":
    case "VALIDATION_ERROR":
      errorString = getLocalizedString("validationError");
      break;
    case "INVALID_DATES":
      errorString = getLocalizedString("datesInvalid");
      break;
    case "DUPLICATED_DATA":
      errorString = getLocalizedString("objectiveAlreadyExistsNameOrCode");
      break;
    case "IN_USE":
      errorString = getLocalizedString("objectiveInUse");
      break;
    case "WEIGHTING_EXCEEDED":
      errorString = getLocalizedString("objectiveWeightWarning2");
      break;
    case "IS_EVALUATED":
      errorString = getLocalizedString("objectiveAlreadyEvaluated", { evaluationName: e.response.data?.evaluationName });
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

export const getGoalsAndObjectivesAction = (period) => async (dispatch) => {
  dispatch({ type: actionTypes.GET });
  try {
    let response = await getGoalsAndObjectivesAPI((period && period !== "-") ? `?period=${period}` : '');

    let data = response.data && response.data.data;
    let collaboratorList = response.data && response.data.collaboratorList;
    let enabledCollaboratorActions = response.data && response.data.enabledCollaboratorActions;
    let periodList = response.data && response.data.periodList;
    let statusList = response.data && response.data.statusList;
    let targetTypeList = response.data && response.data.targetTypeList;
    let targetList = response.data && response.data.targetList;
    let verificationFailures = response.data && response.data.verificationFailures;
    let manager = response.data && response.data.manager;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_FULFILLED,
      payload: {
        data,
        collaboratorList,
        enabledCollaboratorActions,
        periodList,
        statusList,
        targetTypeList,
        targetList,
        verificationFailures,
        manager,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const deleteGoalsAndObjectivesAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE });
  try {
    let response = await deleteGoalsAndObjectivesAPI(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("objectiveDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const createGoalsAndObjectivesAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE });
  try {
    let response = await createGoalsAndObjectivesAPI(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("objectiveCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const modifyGoalsAndObjectivesAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.MODIFY });
  try {
    let response = await modifyGoalsAndObjectivesAPI(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.MODIFY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.MODIFY_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("objectiveModifiedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.MODIFY_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const verifyGoalsAndObjectivesAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.VERIFY });
  try {
    let response = await verifyGoalsAndObjectivesAPI(dataToSend);
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.VERIFY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.VERIFY_FULFILLED,
    });

    return response;
  } catch (e) {
    dispatch({ type: actionTypes.VERIFY_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const getGoalsAndObjectivesTasksAction = (code) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_TASKS });
  try {
    let response = await getGoalsAndObjectivesTasksAPI(`?code=${code}`);

    let taskList = response.data && response.data.taskList;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_TASKS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_TASKS_FULFILLED,
      payload: {
        taskList,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_TASKS_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const getGoalsAndObjectivesOneAction = (internalCode) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_TASKS });
  try {
    let response = await getGoalsAndObjectivesOneAPI(`?internalCode=${internalCode}`);

    let taskList = response.data && response.data.taskList;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_TASKS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_TASKS_FULFILLED,
      payload: {
        taskList,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_TASKS_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};